import { useState } from "react";
import { motion } from "framer-motion";
import { MessageSquareHeart, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { submitFeedback } from "@/lib/api";
import { loadTraineeName, loadCohort } from "@/lib/progression";

const RatingRow = ({ label, value, onChange, testId }) => (
  <div>
    <p className="text-sm font-medium text-stone-700">{label}</p>
    <div className="mt-2 flex gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`${testId}-${n}`}
          onClick={() => onChange(n)}
          aria-label={`${label}: ${n} of 5`}
          className={`h-10 w-10 rounded-full text-sm font-semibold transition-colors duration-150 ${
            value === n ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-emerald-100"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

export const FeedbackSurvey = ({ onDone }) => {
  const [realism, setRealism] = useState(0);
  const [preparedness, setPreparedness] = useState(0);
  const [mostValuable, setMostValuable] = useState("");
  const [changeSuggestion, setChangeSuggestion] = useState("");
  const [recommend, setRecommend] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!realism || !preparedness || recommend === null) {
      toast.error("Please answer the two ratings and the yes/no question.");
      return;
    }
    setBusy(true);
    try {
      await submitFeedback({
        trainee_name: loadTraineeName(),
        cohort: loadCohort(),
        realism,
        preparedness,
        most_valuable: mostValuable,
        change_suggestion: changeSuggestion,
        would_recommend: recommend,
      });
      toast.success("Thank you. Your feedback shapes this training.");
      onDone();
    } catch {
      toast.error("Could not save your feedback. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 py-10 px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="feedback-survey"
        className="max-w-xl mx-auto rounded-2xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm"
      >
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <MessageSquareHeart className="h-4 w-4" /> One last thing
        </div>
        <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold text-stone-900">
          How was your shift?
        </h2>
        <p className="mt-2 text-base leading-relaxed text-stone-600">
          Two minutes of your honest feedback helps make this training better for the next advocate.
        </p>

        <div className="mt-7 space-y-6">
          <RatingRow label="How realistic did the cases feel?" value={realism} onChange={setRealism} testId="rating-realism" />
          <RatingRow label="How much more prepared do you feel than before training?" value={preparedness} onChange={setPreparedness} testId="rating-preparedness" />

          <div>
            <p className="text-sm font-medium text-stone-700">Which case or moment was most valuable to you?</p>
            <Textarea
              data-testid="feedback-valuable-input"
              value={mostValuable}
              onChange={(e) => setMostValuable(e.target.value)}
              placeholder="A case, a choice, a piece of feedback..."
              className="mt-2 min-h-[80px]"
            />
          </div>

          <div>
            <p className="text-sm font-medium text-stone-700">What is missing, or what would you change?</p>
            <Textarea
              data-testid="feedback-change-input"
              value={changeSuggestion}
              onChange={(e) => setChangeSuggestion(e.target.value)}
              placeholder="Be as blunt as you like; it helps."
              className="mt-2 min-h-[80px]"
            />
          </div>

          <div>
            <p className="text-sm font-medium text-stone-700">Would you recommend this training to other advocates?</p>
            <div className="mt-2 flex gap-3">
              <button
                type="button"
                data-testid="recommend-yes"
                onClick={() => setRecommend(true)}
                className={`rounded-full px-6 py-2 text-sm font-medium transition-colors duration-150 ${
                  recommend === true ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-emerald-100"
                }`}
              >
                Yes
              </button>
              <button
                type="button"
                data-testid="recommend-no"
                onClick={() => setRecommend(false)}
                className={`rounded-full px-6 py-2 text-sm font-medium transition-colors duration-150 ${
                  recommend === false ? "bg-stone-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                No
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Button
            data-testid="feedback-submit-button"
            onClick={submit}
            disabled={busy}
            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 h-11"
          >
            <Send className="mr-1.5 h-4 w-4" /> {busy ? "Sending…" : "Send feedback"}
          </Button>
          <button
            data-testid="feedback-skip-link"
            onClick={onDone}
            className="text-sm font-medium text-stone-500 hover:text-emerald-800 underline underline-offset-4 transition-colors duration-200"
          >
            Skip for now
          </button>
        </div>
      </motion.div>
    </div>
  );
};
